// Hosted Model Serving (Part II) — Admin → Model Serving. Add open-weight models
// from Hugging Face (weights copied to the tenant's S3 library once), warm them up
// on a chosen GPU, park them to $0, update / roll back versions. Spec:
// docs/design/hosted-model-serving-v2-ui-acceptance.md (mockup
// hosted-model-serving-ui-mockup-v1.html). View logic: modelServingModel.js.
import { useEffect, useRef, useState } from "react";
import { modelServingApi } from "../../api/client";
import { COLORS } from "../../theme/colors";
import AddModelWizard from "./AddModelWizard";
import ModelDrawer from "./ModelDrawer";
import { ConfirmDialog, RemoveDialog, StopDialog } from "./ModelDialogs";
import {
  cardSpecs, fmtGB, inFlightRevision, modelStatus, money, pageStats, parksText, retryableRevision, versionText, warmSteps,
} from "./modelServingModel";
import { Btn, Dot, MONO, Notice, SectionHead, card, errorText } from "./modelServingUi";
import UpdateDialog from "./UpdateDialog";
import { useModelServing } from "./useModelServing";
import { LibraryHead, LibraryRow, libraryLayout } from "./modelServingLibrary";
import { useElementWidth } from "../../hooks/useElementWidth";
import WarmUpDialog from "./WarmUpDialog";

const FLOW = [
  ["Once per version", "Look up on Hugging Face", "Paste a repo id. We read its size, format and license and recommend a GPU."],
  ["Once per version", "Copy to your S3", "Weights download from Hugging Face into your library — the only time Hugging Face is contacted."],
  ["On demand", "Warm up", "Press Warm up, or just send a request. A GPU node starts, copies the weights from your S3 and loads the model."],
  ["While in use", "Serve", "Agents use it like any model in your rankings."],
];

function Stat({ k, v, unit, sub }) {
  return (
    <div style={card()} data-testid="ms-stat">
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6, color: COLORS.textDim }}>{k}</div>
      <div style={{ fontSize: 24, fontWeight: 700, margin: "6px 0 2px", fontVariantNumeric: "tabular-nums" }}>
        {v}{unit && <small style={{ fontSize: 13, color: COLORS.textDim, marginLeft: 3 }}>{unit}</small>}
      </div>
      <div style={{ fontSize: 12, color: COLORS.textMuted }}>{sub}</div>
    </div>
  );
}

function RunningCard({ model, preset, onDetails, onStop }) {
  const status = modelStatus(model);
  const serving = model.serving;          // running/warming models always carry their live status
  const warming = status === "warming";
  return (
    <div data-testid={`running-${model.key}`} style={card()}>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{model.display_name}</div>
          <div style={{ fontSize: 12, color: COLORS.textDim, fontFamily: MONO }}>{model.key} · version {versionText(model)}</div>
        </div>
        <button type="button" aria-label="Model details" onClick={onDetails}
          style={{ border: `1px solid ${COLORS.border}`, background: "transparent", color: COLORS.text, borderRadius: 8,
            cursor: "pointer", padding: "0 8px" }}>⋯</button>
      </div>
      <div style={{ margin: "12px 0", display: "grid", gap: 6, fontSize: 12.5 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
          <Dot tone={warming ? "amber" : "green"} pulse={warming} /><b>{warming ? "Warming up" : "Ready"}</b>
        </span>
        {warming
          ? (
            <span data-lc="warm-steps" style={{ display: "flex", gap: 10, flexWrap: "wrap", fontSize: 11.5 }}>
              {warmSteps(serving).map((s) => (
                <span key={s.key} data-lc={s.key === "copying" && s.state === "active" ? "warm-progress" : undefined}
                  data-state={s.state}
                  style={{ color: s.state === "done" ? COLORS.green : s.state === "active" ? COLORS.orange : COLORS.textDim,
                    fontWeight: s.state === "active" ? 700 : 500 }}>
                  {s.state === "done" ? "✓ " : s.state === "active" ? "⋯ " : "○ "}{s.label}
                </span>
              ))}
            </span>
          )
          : <span data-lc="idle-countdown" style={{ color: COLORS.textMuted }}>{parksText(serving, model.idle_timeout_minutes || 15)}</span>}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
        {cardSpecs(model, preset).map(([k, v]) => (
          <div key={k}>
            <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.5, color: COLORS.textDim }}>{k}</div>
            <div style={{ fontSize: 12.5 }}>{v}</div>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <Btn sm onClick={onDetails}>Details</Btn>
        <div style={{ flex: 1 }} />
        <Btn sm kind="danger" data-lc={warming ? "cancel-warm" : undefined} onClick={onStop}>{warming ? "Cancel warm-up" : "Stop"}</Btn>
      </div>
    </div>
  );
}

export default function ModelServingTab() {
  const { models, error, presets, presetsError, load, stamp } = useModelServing();
  const [dialog, setDialog] = useState(null);      // {kind, key, ...}
  const [drawer, setDrawer] = useState(null);      // {key, tab}
  const [pageErr, setPageErr] = useState(null);
  const list = models || [];
  // the library lays itself out for the width it has (table → compact → cards)
  const libraryRef = useRef(null);
  const layout = libraryLayout(useElementWidth(libraryRef));
  const byKey = (k) => list.find((m) => m.key === k);

  // "Manage in Model Serving" (Local LLM) lands here with ?model=<key>: open it, once.
  const opened = useRef(false);
  useEffect(() => {
    if (opened.current || models === null) return;
    opened.current = true;
    const key = new URLSearchParams(window.location.search).get("model");
    if (key && models.some((m) => m.key === key)) setDrawer({ key, tab: "overview" });
  }, [models]);
  const stats = pageStats(list, presets);
  const live = list.filter((m) => ["running", "warming"].includes(modelStatus(m)));
  const idle = stats.idleMinutes;

  // Run an action, then reload; errors propagate to the caller (drawer / row).
  const act = async (fn) => {
    try {
      if (fn) await fn();
    } finally {
      await load();
    }
  };
  const rowAct = async (fn) => {
    setPageErr(null);
    try {
      await act(fn);
    } catch (e) {
      setPageErr(errorText(e));
    }
  };

  const onAction = (a, m) => {
    if (a === "details") setDrawer({ key: m.key, tab: modelStatus(m) === "warm_failed" ? "activity" : "overview" });
    else if (a === "stop") setDialog({ kind: "stop", key: m.key });
    else if (a === "warm" || a === "retry_warm") setDialog({ kind: "warm", key: m.key });
    else if (a === "remove") setDialog({ kind: "remove", key: m.key });
    else if (a === "retry_download") rowAct(() => modelServingApi.retryDownload(m.key, retryableRevision(m).id));
    else {
      const rev = inFlightRevision(m);
      setDialog({
        kind: "confirm", key: m.key, title: `Cancel download of ${m.display_name}?`, actionLabel: "Cancel download",
        body: `Stops the download and deletes the ${fmtGB(rev.bytes_done || 0)} already copied to S3. You can retry it or remove the model later.`,
        action: () => modelServingApi.cancelDownload(m.key, rev.id),
      });
    }
  };

  const dModel = dialog && byKey(dialog.key);
  const close = () => setDialog(null);
  const done = () => load();
  const drawerModel = drawer && byKey(drawer.key);

  return (
    <div data-testid="model-serving-tab" style={{ color: COLORS.text }}>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 16, marginBottom: 18, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <h2 style={{ margin: "0 0 4px", fontSize: 20 }}>Model Serving</h2>
          <div style={{ color: COLORS.textMuted, fontSize: 13, maxWidth: 720, lineHeight: 1.5 }}>
            Run open-weight models from Hugging Face on your own GPU. Weights are copied to your S3 library once, models
            scale to zero when idle, and a parked model warms itself on the next request.
          </div>
        </div>
        <Btn kind="primary" data-lc="add" onClick={() => setDialog({ kind: "add" })}>＋ Add from Hugging Face</Btn>
      </div>

      {error && <Notice testid="page-error" tone="bad" icon="!">{`Couldn't load your models: ${error}`}</Notice>}
      {pageErr && <Notice testid="action-error" tone="bad" icon="!">{pageErr}</Notice>}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 12, marginBottom: 6 }}>
        <Stat k="Running now" v={<>{stats.running} <small style={{ fontSize: 13, color: COLORS.textDim }}>/ {stats.warming} warming</small></>}
          sub={`${stats.gpuNodes} GPU node${stats.gpuNodes === 1 ? "" : "s"} active`} />
        <Stat k="In your library" v={stats.library}
          sub={`${stats.ready} ready · ${stats.downloading} downloading · ${stats.failed} failed`} />
        <Stat k="S3 weights" v={stats.unsized && !stats.s3Bytes ? "—" : fmtGB(stats.s3Bytes).split(" ")[0]}
          unit={stats.unsized && !stats.s3Bytes ? undefined : fmtGB(stats.s3Bytes).split(" ")[1]}
          sub={`across ${stats.versions} version${stats.versions === 1 ? "" : "s"}${stats.unsized ? ` · ${stats.unsized} not measured yet` : ""}`} />
        <Stat k="GPU spend (now)" v={presetsError ? "—" : money(stats.spendPerHour)} unit={presetsError ? undefined : "/hr"}
          sub={presetsError ? "GPU prices unavailable right now" : `idle models park after ${idle} min`} />
      </div>

      <SectionHead title="Running now" count={live.length} />
      {models === null ? <div style={{ color: COLORS.textDim }}>Loading…</div> : live.length === 0
        ? <div style={{ ...card(), color: COLORS.textDim, fontSize: 13 }}>Nothing is running — every model is parked at $0.</div>
        : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(min(320px,100%),1fr))", gap: 14 }}>
            {live.map((m) => (
              <RunningCard key={m.key} model={m} preset={presets[m.node_class]}
                onDetails={() => setDrawer({ key: m.key, tab: "overview" })}
                onStop={() => setDialog({ kind: "stop", key: m.key })} />
            ))}
          </div>
        )}

      <SectionHead title="Your model library" count={list.length} />
      <div ref={libraryRef} data-testid="ms-library" data-layout={layout} style={{ ...card(), padding: 0, overflowX: "auto" }}>
        {layout === "cards" ? (
          <div role="list" style={{ display: "grid" }}>
            {list.map((m) => <LibraryRow key={m.key} model={m} preset={presets[m.node_class]} layout={layout} onAction={onAction} />)}
            {models !== null && list.length === 0 && (
              <div style={{ padding: 18, color: COLORS.textDim }}>No models yet — add one from Hugging Face to get started.</div>
            )}
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <LibraryHead layout={layout} />
            <tbody>
              {list.map((m) => <LibraryRow key={m.key} model={m} preset={presets[m.node_class]} layout={layout} onAction={onAction} />)}
              {models !== null && list.length === 0 && (
                <tr><td colSpan={layout === "table" ? 6 : 4} style={{ padding: 18, color: COLORS.textDim }}>
                  No models yet — add one from Hugging Face to get started.
                </td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <SectionHead title="How hosting works" />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 10 }}>
        {[...FLOW, [`Stop, or ${idle} min idle`, "Park → $0",
          "The GPU is released. Warm up again anytime — from S3, not Hugging Face."]].map(([when, title, text]) => (
          <div key={title} data-testid="ms-flow-step" style={{ ...card(), padding: 14, fontSize: 12.5, color: COLORS.textMuted }}>
            <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.5, color: COLORS.accentText }}>{when}</div>
            <b style={{ display: "block", color: COLORS.text, margin: "4px 0", fontSize: 13.5 }}>{title}</b>{text}
          </div>
        ))}
      </div>

      {drawerModel && (
        <ModelDrawer key={drawerModel.key} model={drawerModel} preset={presets[drawerModel.node_class]}
          initialTab={drawer.tab} stamp={stamp} onClose={() => setDrawer(null)} onAct={act}
          onDialog={(kind, extra) => setDialog({ kind, key: drawerModel.key, ...extra })} />
      )}

      {dialog?.kind === "add" && <AddModelWizard onClose={close} onAdded={done} />}
      {dModel && dialog.kind === "warm" && <WarmUpDialog model={dModel} onClose={close} onDone={done} />}
      {dModel && dialog.kind === "stop" && <StopDialog model={dModel} onClose={close} onDone={done} />}
      {dModel && dialog.kind === "remove" && (
        <RemoveDialog model={dModel} onClose={close}
          onDone={() => { if (drawer?.key === dModel.key) setDrawer(null); done(); }} />
      )}
      {dModel && (dialog.kind === "update" || dialog.kind === "switch") && (
        <UpdateDialog mode={dialog.kind === "switch" ? "switch" : "pick"} model={dModel} onClose={close} onDone={done} />
      )}
      {dModel && dialog.kind === "confirm" && (
        <ConfirmDialog title={dialog.title} body={dialog.body} actionLabel={dialog.actionLabel} action={dialog.action}
          onClose={close} onDone={done} />
      )}
    </div>
  );
}
